import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { BedDouble, Plus, Search } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_FILTERS = ["All", "enquiry", "confirmed", "modified", "cancelled"];

const STATUS_COLORS = {
  enquiry:   "bg-yellow-100 text-yellow-800",
  confirmed: "bg-green-100 text-green-800",
  modified:  "bg-blue-100 text-blue-800",
  cancelled: "bg-red-100 text-red-800",
};

const PAYMENT_COLORS = {
  pending:          "bg-gray-100 text-gray-600",
  awaiting_payment: "bg-orange-100 text-orange-700",
  paid:             "bg-green-100 text-green-700",
  refunded:         "bg-purple-100 text-purple-700",
  not_requested:    "bg-gray-100 text-gray-500",
};

function calcNights(checkIn, checkOut) {
  try {
    const diff = new Date(checkOut) - new Date(checkIn);
    const n = Math.round(diff / 86400000);
    return n > 0 ? n : 0;
  } catch { return 0; }
}

export default function HotelBookings() {
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", hotel_name: "",
    check_in: "", check_out: "",
    selling_price_per_night: "", supplier_price_per_night: "",
    num_rooms: "1",
    room_type: "", currency: "EUR",
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/hotels`, { headers: getAuthHeaders() });
      setHotels(r.data || []);
    } catch { /**/ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = hotels.filter(h => {
    if (statusFilter !== "All" && h.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        h.customer_name?.toLowerCase().includes(q) ||
        h.hotel_name?.toLowerCase().includes(q) ||
        h.invoice_number?.toLowerCase().includes(q) ||
        h.hotel_city?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const nights = calcNights(form.check_in, form.check_out);
  const numRooms = parseInt(form.num_rooms) || 1;
  const sellingPerNight = parseFloat(form.selling_price_per_night) || 0;
  const supplierPerNight = parseFloat(form.supplier_price_per_night) || 0;
  const sellingTotal = nights > 0 && sellingPerNight > 0 ? (sellingPerNight * nights * numRooms).toFixed(2) : null;
  const supplierTotal = nights > 0 && supplierPerNight > 0 ? (supplierPerNight * nights * numRooms).toFixed(2) : null;
  const profit = sellingTotal && supplierTotal ? (parseFloat(sellingTotal) - parseFloat(supplierTotal)).toFixed(2) : null;

  const createHotel = async () => {
    setCreateError("");
    if (!form.customer_name || !form.hotel_name || !form.check_in || !form.check_out) {
      setCreateError("Please fill in required fields: Customer, Hotel, Check-in, Check-out");
      return;
    }
    setCreating(true);
    try {
      const r = await axios.post(`${API}/hotels`, {
        ...form,
        num_rooms: parseInt(form.num_rooms) || 1,
        selling_price_per_night: sellingPerNight || null,
        supplier_price_per_night: supplierPerNight || null,
      }, { headers: getAuthHeaders() });
      navigate(`/hotel-bookings/${r.data.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (Array.isArray(detail)) {
        setCreateError(detail.map(d => d.msg || JSON.stringify(d)).join("; "));
      } else {
        setCreateError(typeof detail === "string" ? detail : "Failed to create hotel booking. Check required fields.");
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <DashboardLayout>
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
            <BedDouble size={20} className="text-indigo-700" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Hotel Bookings</h1>
            <p className="text-sm text-gray-500">{hotels.length} booking{hotels.length !== 1 ? "s" : ""}</p>
          </div>
        </div>
        <button
          onClick={() => { setShowCreate(true); setCreateError(""); }}
          className="flex items-center gap-2 bg-indigo-700 hover:bg-indigo-800 text-white rounded-xl px-4 py-2 text-sm font-semibold"
        >
          <Plus size={16} /> New Hotel Booking
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-48">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search customer, hotel, ref..."
            className="pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm w-full"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          {STATUS_FILTERS.map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold capitalize border transition-colors ${
                statusFilter === s
                  ? "bg-indigo-700 text-white border-indigo-700"
                  : "bg-white text-gray-600 border-gray-200 hover:border-indigo-300"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-16 text-gray-400">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">No hotel bookings found.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {["Ref", "Guest", "Hotel", "Check-in", "Check-out", "Nights", "Rooms", "Status", "Payment", ""].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(h => (
                  <tr key={h.id} className="hover:bg-indigo-50/30 transition-colors cursor-pointer" onClick={() => navigate(`/hotel-bookings/${h.id}`)}>
                    <td className="px-4 py-3 font-mono text-xs text-indigo-700 font-semibold">{h.invoice_number || "—"}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{h.customer_name}</td>
                    <td className="px-4 py-3 text-gray-700">
                      <div>{h.hotel_name}</div>
                      {h.hotel_city && <div className="text-xs text-gray-400">{h.hotel_city}{h.hotel_country ? `, ${h.hotel_country}` : ""}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{h.check_in}</td>
                    <td className="px-4 py-3 text-gray-600">{h.check_out}</td>
                    <td className="px-4 py-3 text-gray-600">{calcNights(h.check_in, h.check_out) || "—"}</td>
                    <td className="px-4 py-3 text-gray-600">{h.num_rooms || 1}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${STATUS_COLORS[h.status] || "bg-gray-100 text-gray-600"}`}>
                        {h.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${PAYMENT_COLORS[h.payment_status] || PAYMENT_COLORS.pending}`}>
                        {(h.payment_status || "pending").replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-indigo-600 text-xs font-semibold">View →</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create modal */}
      <Dialog open={showCreate} onOpenChange={open => { if (!open) { setShowCreate(false); setCreateError(""); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Hotel Booking</DialogTitle>
          </DialogHeader>
          <div>

            {createError && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                {createError}
              </div>
            )}

            <div className="space-y-4">
              {[
                { key: "customer_name", label: "Customer Name *", type: "text" },
                { key: "customer_email", label: "Customer Email", type: "email" },
                { key: "hotel_name", label: "Hotel Name *", type: "text" },
                { key: "room_type", label: "Room Type", type: "text" },
                { key: "check_in", label: "Check-in *", type: "date" },
                { key: "check_out", label: "Check-out *", type: "date" },
                { key: "num_rooms", label: "Number of Rooms", type: "number" },
              ].map(f => (
                <div key={f.key}>
                  <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{f.label}</label>
                  <input
                    type={f.type}
                    min={f.type === "number" ? "1" : undefined}
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    value={form[f.key]}
                    onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                  />
                </div>
              ))}

              {/* Per-night pricing with auto-calc */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 space-y-3">
                <p className="text-xs font-bold text-indigo-800 uppercase tracking-wide">Pricing (per night)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Selling Price / Night (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                      value={form.selling_price_per_night}
                      onChange={e => setForm(p => ({ ...p, selling_price_per_night: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Supplier Cost / Night (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                      value={form.supplier_price_per_night}
                      onChange={e => setForm(p => ({ ...p, supplier_price_per_night: e.target.value }))}
                    />
                  </div>
                </div>
                {nights > 0 && (
                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="bg-white rounded-lg p-2">
                      <p className="text-gray-500">Nights × Rooms</p>
                      <p className="font-bold text-gray-900">{nights} × {numRooms}</p>
                    </div>
                    {sellingTotal && (
                      <div className="bg-white rounded-lg p-2">
                        <p className="text-gray-500">Customer Total</p>
                        <p className="font-bold text-green-700">€{sellingTotal}</p>
                      </div>
                    )}
                    {profit && (
                      <div className="bg-white rounded-lg p-2">
                        <p className="text-gray-500">Profit</p>
                        <p className={`font-bold ${parseFloat(profit) >= 0 ? "text-green-700" : "text-red-600"}`}>€{profit}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6 justify-end">
              <button onClick={() => { setShowCreate(false); setCreateError(""); }} className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2">Cancel</button>
              <button
                onClick={createHotel}
                disabled={creating}
                className="bg-indigo-700 hover:bg-indigo-800 text-white rounded-xl px-5 py-2 text-sm font-semibold disabled:opacity-50"
              >
                {creating ? "Creating..." : "Create Booking"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </DashboardLayout>
  );
}
